import { AuthState } from '@aws-amplify/ui-components';
import {
  Box,
  Container,
  Flex,
  Icon,
  Link,
  Menu,
  MenuItem,
  MenuButton,
  MenuList,
  Button,
  Text,
} from '@chakra-ui/react';
import { QuestionOutlineIcon } from '@chakra-ui/icons';
import { routerActions } from 'connected-react-router';
import React, { memo, useEffect, useState } from 'react';
import { FiCalendar, FiLogOut, FiSettings, FiMail, FiBookOpen } from 'react-icons/fi';
import { FaYenSign } from 'react-icons/fa';
import { TiArrowSortedDown } from 'react-icons/ti';
import { useAppDispatch, useAppSelector } from '../hooks';
import { signOutAsync } from '../slice/userSlice';
import { startLoading, stopLoading } from '../slice/loadingSlice';
import './shell.scss';
import { Auth } from 'aws-amplify';
import logo01 from '../assets/images/logo01.png';
import { URL_PRIVACY, URL_TERMS, LEARNER_V2_URL } from '../configs';

const NavItem = ({ icon, title, active, onClick, children, pathname, is_external, href }: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = Array.isArray(children) && children.length > 0;

  return (
    <Flex mt={3} flexDir="column" w="100%">
      <Link
        backgroundColor={active ? '#cbe7ff' : 'inherit'}
        p={3}
        borderRadius={2}
        _hover={{ textDecor: 'none', backgroundColor: '#e8f4ff' }}
        _focus={{ boxShadow: 'none' }}
        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
          (e.currentTarget as HTMLElement).blur();
          if (hasChildren) {
            setIsOpen(!isOpen);
          } else if (!is_external) {
            onClick();
          }
        }}
        href={is_external ? href : undefined}
        target={is_external ? '_blank' : undefined}
      >
        <Flex align="center" justify="space-between">
          <Flex align="center">
            {icon && <Icon as={icon} fontSize="xl" color={active ? '#82AAAD' : 'gray.500'} />}
            <Text ml={5}>{title}</Text>
          </Flex>
          {hasChildren && (
            <TiArrowSortedDown
              style={{
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          )}
        </Flex>
      </Link>

      {/* Sub-navigation */}
      {hasChildren && isOpen && (
        <Box ml={8} mt={1}>
          {children.map((child: any) => (
            <Link
              key={child.title}
              display="block"
              p={2}
              borderRadius={2}
              backgroundColor={child.pathname === pathname ? '#dff1ff' : 'inherit'}
              _hover={{ backgroundColor: '#f0faff' }}
              _focus={{ boxShadow: 'none' }}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                (e.currentTarget as HTMLElement).blur();
                if (!child.is_external) {
                  return onClick(child.pathname);
                }
              }}
              href={child.is_external ? child.pathname : undefined}
              target={child.is_external ? '_blank' : undefined}
            >
              {child.title}
            </Link>
          ))}
        </Box>
      )}
    </Flex>
  );
};

const navs = [
  {
    icon: FiCalendar,
    title: 'レッスン',
    active: false,
    pathname: '#',
    children: [
      {
        title: 'レッスン一覧',
        pathname: '/lessons',
      },
      {
        title: 'フィードバック一覧',
        pathname: '/feedback',
      },
      /* {
        title: '申し送り一覧',
        pathname: '/lesson_notes',
      }, */
    ],
  },
  {
    icon: FaYenSign,
    title: '売上',
    active: false,
    pathname: '#',
    children: [
      {
        title: '月別レッスン料一覧',
        pathname: '/fees',
      },
    ],
  },
  {
    icon: FiSettings,
    title: '設定',
    active: false,
    pathname: '#',
    children: [
      {
        title: 'プロフィール',
        pathname: '/profile',
      },
      {
        title: '利用契約',
        pathname: URL_TERMS,
        is_external: true,
      },
      {
        title: 'プライバシーポリシー',
        pathname: URL_PRIVACY,
        is_external: true,
      },
    ],
  },
  {
    icon: FiBookOpen,
    title: '学習者アプリ',
    active: false,
    pathname: `${LEARNER_V2_URL}/teacher/top`,
    is_external: true,
  },
  {
    icon: FiMail,
    title: 'お問い合わせ',
    active: false,
    pathname: '/contact',
  },
];
export interface IShellProps {
  children: React.ReactNode;
}

export var orgKey = React.createContext('kokoka');
export var currentUserId = React.createContext('');

const getAuthenticateInfo = async (attributeName: string = 'currentUserId') => {
  const authenticatedUser = await Auth.currentAuthenticatedUser().catch((e) => null);
  if (authenticatedUser) {
    const currentUserId = await authenticatedUser.attributes['sub'];
    return currentUserId;
  }
  return '';
};

export const Shell: React.NamedExoticComponent<IShellProps> = memo(({ children }) => {
  const dispatch = useAppDispatch();
  const historyState = useAppSelector((state) => state.router);
  const userState = useAppSelector((state) => state.user);
  const [companyId, setCompanyId] = useState<any>('くらしスタディ はなすプラス');
  const onClickBrand = () => dispatch(routerActions.push({ pathname: '/top' }));

  // サインアウトはトークンの失効で通信が発生して数秒かかるため、その間は画面全体を読み込み中にして操作できないようにする
  const onClickSignout = async () => {
    dispatch(startLoading());
    try {
      await dispatch(signOutAsync());
    } finally {
      dispatch(stopLoading());
    }
  };

  useEffect(() => {
    getAuthenticateInfo().then((teacherId) => {
      currentUserId = React.createContext(teacherId);
    });
    return;
  });

  return (
    <>
      <Box
        display="flex"
        height="60px"
        borderBottom="1px solid #ddd"
        justifyContent="space-between"
        boxShadow={'sm'}
        alignItems="center"
        position="fixed"
        top="0"
        left="0"
        right="0"
        zIndex={500}
        bg="white"
      >
        <Box alignItems="center" display="flex" flexWrap="wrap">
          <Box className="Logo" onClick={onClickBrand} style={{ cursor: 'pointer' }}>
            <img src={logo01} alt={companyId} />
          </Box>
          <br />
          <sub style={{ color: '#3f83bf', marginLeft: '5px', bottom: 'unset' }}>
            | レッスン管理システム
          </sub>
        </Box>
        <Box hidden={!(userState.authState === AuthState.SignedIn && userState.user)}>
          <Flex>
            <Menu>
              <MenuButton
                as={Button}
                fontSize="12px"
                fontWeight={500}
                marginRight={3}
                height={'32px'}
                background={'white'}
                border="1px solid"
                borderColor="gray.300"
                borderRadius="4px"
                padding="1px 12px"
                rightIcon={<TiArrowSortedDown size={13} />}
                _hover={{ background: 'white', borderColor: 'gray.500' }}
                _focus={{ background: 'white', boxShadow: 'none' }}
                _active={{ background: 'white' }}
              >
                {userState.user?.email}
              </MenuButton>
              <MenuList>
                <MenuItem
                  as={Button}
                  fontSize="14px"
                  fontWeight={400}
                  leftIcon={<Icon as={FiLogOut} boxSize="22px" color="red.500" fontWeight="bold" />}
                  background={'white'}
                  onClick={onClickSignout}
                  color="red.500"
                  justifyContent="flex-start"
                  ml={'7px'}
                  _hover={{ background: 'transparent', color: 'red.500' }}
                  _focus={{ background: 'transparent', boxShadow: 'none' }}
                  _active={{ background: 'transparent' }}
                >
                  Signout
                </MenuItem>
              </MenuList>
            </Menu>
            {/* <Box>
              <a href="#">
                <div
                  style={{
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    outline: 'none',
                  }}
                >
                  <QuestionOutlineIcon boxSize={7} color="#3182ce" />
                </div>
              </a>
            </Box> */}
          </Flex>
        </Box>
      </Box>
      <Flex>
        <Flex
          pos="sticky"
          borderRight="1px solid #ddd"
          boxShadow="sm"
          minW="210px"
          flexDir="column"
          justifyContent="space-between"
          top="0"
          h="100vh"
          overflowY="auto"
          hidden={!(userState.authState === AuthState.SignedIn && userState.user)}
        >
          <Flex className="scrollable-sidebar" flexDir="column" marginTop={'60px'} as="nav">
            <>
              {navs.map((nav) => (
                <NavItem
                  key={nav.title}
                  icon={nav.icon}
                  title={nav.title}
                  active={nav.pathname === historyState.location.pathname}
                  pathname={historyState.location.pathname}
                  onClick={(pathname?: string) => {
                    dispatch(routerActions.push(pathname || nav.pathname));
                  }}
                  children={nav.children}
                  is_external={nav.is_external}
                  href={nav.pathname}
                />
              ))}
            </>
          </Flex>
        </Flex>
        <Container
          className="Shell-children main-content"
          marginTop={'55px'}
          maxWidth={'100vw'}
          marginX={'2em'}
        >
          <>{children}</>
        </Container>
      </Flex>
    </>
  );
});
